import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import HeroTitle from './components/heroTitle'

import { Location } from '@reach/router'
import { defaultCTAURI } from 'src/modules/utils/constants'
import { useSelector } from 'react-redux'
import { selectCTAParams } from 'src/modules/reducers/params'
import { createUrl } from 'src/modules/utils/createUrl'
import { replaceUrlParam } from 'src/modules/utils/replaceUrlParam'
import LivePreviewPlayer from 'src/components/common/LivePreviewPlayer'
import { fireUIInteractionEvent } from 'src/modules/analytics/v3'
import { selectStateForProtobuf } from 'src/modules/reducers'
import Image from '../Image'
import { useCohortFlag } from 'src/modules/hooks/useCohortFlag'
import { WEB_MARKETING_LIVE_PREVIEW_DURATION } from 'src/modules/reducers/cohorts/constants'
import { selectStacFlagsReady } from 'src/modules/reducers/cohorts'
import { useCtaText } from 'src/modules/hooks/useCtaText'
import { colors, mediaQuery, spacing } from 'src/constants/marketing-ds'
import CTAButton from '../CTAButtonNew'

const HeroLivePlayer = ({
  title,
  subTitle,
  ctaText,
  ctaHrefOverride,
  subhead,
  background = 'https://assets-imgx.fubo.tv/marketing/lp/images/posters/live_preview_bg-2.jpg?w=1920&ch=width&auto=format,compress',
  logo = 'https://assets-imgx.fubo.tv/marketing/lp/images/logos/Fubo_Primary_Logo_FC_RGB.png?w=300',
  logoHeight,
  callsign,
  lang,
  networkName,
  loadingText,
  market,
  disableFreeTrial,
}) => {
  // get ctaParams from redux
  const stateForProtobuf = useSelector(selectStateForProtobuf)
  const parsedParams = useSelector(selectCTAParams)
  let ctaURL = createUrl(defaultCTAURI, parsedParams)
  const ctaCopy = useCtaText({ lang, market, disableFreeTrial, ctaText })

  ctaURL = replaceUrlParam(ctaURL, 'comp', 'hero')

  const livePreviewDuration = useCohortFlag(WEB_MARKETING_LIVE_PREVIEW_DURATION)
  const isCohortReady = useSelector(selectStacFlagsReady)

  const [previewLen, setPreviewLen] = useState(null)

  const ctaLink = ctaHrefOverride || ctaURL
  useEffect(() => {
    if (livePreviewDuration !== 'default') {
      setPreviewLen(parseInt(livePreviewDuration, 10))
    }
  }, [livePreviewDuration])

  const handleOnClick = () => {
    const isAnchor = ctaLink.startsWith('#')
    fireUIInteractionEvent(
      {
        element: ctaCopy,
        component: 'hero_section_cta_clicked',
        type: isAnchor ? 'clicked_anchor' : 'clicked',
      },
      stateForProtobuf
    )
  }

  return (
    <Location>
      {({ location }) => (
        <StyledHeroLivePlayer backgroundVideo={background} $logoHeight={logoHeight}>
          <div className="logo-wrapper">
            <Image src={logo} alt="Fubo Logo" width="100%" />
          </div>
          <div className="hero-content">
            <HeroTitle title={title} subtitle={subTitle} location={location} />
            <div className="hero-video-container">
              <LivePreviewPlayer
                ctaText={ctaText}
                ctaSubhead={subhead}
                callsign={callsign}
                lang={lang}
                networkName={networkName}
                loadingText={loadingText}
                market={market}
                previewLen={previewLen}
                isLoading={!isCohortReady}
              />
            </div>
            <CTAButton link={ctaLink} onClick={handleOnClick}>
              {ctaCopy}
            </CTAButton>
          </div>
        </StyledHeroLivePlayer>
      )}
    </Location>
  )
}

export default HeroLivePlayer

const StyledHeroLivePlayer = styled.div`
  background: ${({ backgroundImage }) =>
    backgroundImage ? `url(${backgroundImage})` : colors.gradients.midnight.medium};

  background-repeat: no-repeat;
  background-position: center;
  background-size: cover;
  box-sizing: border-box;
  color: #fff;
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;

  padding: ${spacing.xl} ${spacing.xs};

  ${mediaQuery.tablet} {
    padding: ${spacing.xxxl} ${spacing.lg};
    min-height: 600px;
  }

  ${mediaQuery.desktop} {
    padding: ${spacing.xxxl} ${spacing.xl};
  }
  .logo-wrapper {
    position: relative;
    width: auto;
    margin: 0 auto ${spacing.lg};
    box-sizing: border-box;

    img {
      max-width: 100%;
      max-height: ${(props) => props.$logoHeight || '48px'};
      height: 100%;
      width: auto;
    }

    @media (max-width: 768px) {
      text-align: center;
      max-height: ${(props) => props.$logoHeight || '48px'};
    }

    ${mediaQuery.tablet} {
      margin-bottom: ${spacing.xl};
    }

    ${mediaQuery.desktop} {
      position: absolute;
      top: ${spacing.xxxl};
      left: ${spacing.xl};
    }
  }
  .hero-content {
    width: 100%;
    text-align: center;
    position: relative;
    box-sizing: border-box;
    .hero-video-container {
      width: 100%;
      max-width: 900px;
      position: relative;
      margin: 0 auto ${spacing.xl};

      video {
        width: 100%;
        max-width: 900px;
      }
    }
  }
  .buy-link {
    @media (max-width: 768px) {
      display: none;
    }
  }
`
